import { Category, Component } from '@Types';

interface UnrecognisedValues {
	componentName: string;
	values: string[];
}

// A data point needs a score when its score is null/undefined — an
// authoritative "this needs scoring" — or when it is 0 AND still a value we
// could not match to the template (a 0 alone is ambiguous: the user may have
// chosen it). Single rule shared by ScoreDataGrid and the setup screens.
export const valueNeedsScore = (
	score: number | null | undefined,
	value: string | number,
	unrecognisedValues: string[],
): boolean =>
	score == null ||
	(score === 0 && unrecognisedValues.includes(String(value)));

const unrecognisedFor = (
	unrecognisedValues: UnrecognisedValues[],
	componentName: string,
): string[] =>
	unrecognisedValues.find(entry => entry.componentName === componentName)
		?.values ?? [];

// Data points in one component's config metadata that still have no score.
export const countComponentValuesNeedingScore = (
	component: Component,
	unrecognisedValues: UnrecognisedValues[],
): number => {
	const unrecognised = unrecognisedFor(unrecognisedValues, component.name);
	return (component.metadata ?? []).filter(meta =>
		valueNeedsScore(meta.value, meta.key, unrecognised),
	).length;
};

// Same, summed over every component in the given categories. Derived from the
// live config, so it is accurate after a reload and drops as the user scores
// values. Note the setup banner that shows it is itself only visible while the
// template-apply summary is (session state); the per-row "N to score" chips
// are what persist across reloads.
export const countValuesNeedingScore = (
	categories: Category[] | undefined,
	unrecognisedValues: UnrecognisedValues[],
): number =>
	(categories ?? []).reduce(
		(total, category) =>
			total +
			(category.components ?? []).reduce(
				(sum, component) =>
					sum +
					countComponentValuesNeedingScore(
						component,
						unrecognisedValues,
					),
				0,
			),
		0,
	);

export interface ScoreCoverage {
	total: number;
	scored: number;
	pct: number;
}

// Share of a component's data points (metadata entries) that have a score.
// Undefined when the component has no metadata — there is nothing to measure.
export const componentScoreCoverage = (
	component: Component,
	unrecognisedValues: UnrecognisedValues[],
): ScoreCoverage | undefined => {
	const total = component.metadata?.length ?? 0;
	if (total === 0) return undefined;
	const scored =
		total - countComponentValuesNeedingScore(component, unrecognisedValues);
	return { total, scored, pct: Math.round((scored / total) * 100) };
};
